import React, {useEffect, useMemo, useState} from "react";
import {VsiBubble, VsiH4, VsiH5, VsiHr} from "../../../styled/App.styled";
import VsiLabeledSelect from "../../../components/VsiLabeledSelect";
import {Col, Row, Spinner, CardHeader, Card, CardBody} from "reactstrap";
import PieGrid from "../../../components/PieGrid";
import {useDataWithRoute, useTitleFromComponent} from "../../../utils/hooks";
import {useVsiAppContext} from "../../../stores/VsiApp";
import {useSearchParams} from "react-router-dom";
import {executiveOverviewColors} from "../../../utils/chartUtils";
import StackedColumnChart from "../../../components/StackedColumnChart";

const allUsableTypes = [

    {value: "usable", label: "Effective", usedType: 'used', route: "business_unit_executive_overview_proc"},
    {
        value: "physical_usable",
        label: "Physical",
        usedType: 'physical_used',
        route: "business_unit_executive_overview_proc"
    }
];

const groupOptions = [
    {value: 5, label: "Classification", type: "classification"},
    {value: 6, label: "Device type", type: "device_type_code"},
    {value: 4, label: "Data Center", type: "data_center"},
    {value: 7, label: "Operational Category", type: "operational_category"}
];

export default function BUExecutiveOverview({context, title}) {
    useTitleFromComponent(`${context} / ${title}`);
    const {theme, client, periodId} = useVsiAppContext();

    const [chartDataCenter, setChartDataCenter] = useState([]);
    const [chartClassification, setChartClassification] = useState([]);
    const [chartDeviceType, setChartDeviceType] = useState([]);
    const [chartOperationalCat, setChartOperationalCat] = useState([]);
    const [chartData, setChartData] = useState([]);

    const [searchParams, setSearchParams] = useSearchParams();
    const [graphLoading, setGraphLoading] = useState(true);
    const searchValueType = searchParams.get("valueType");
    const defaultValueType = searchValueType ? allUsableTypes.find(({value}) => searchValueType === value) : allUsableTypes[0];
    const [valueType, setValueType] = useState(defaultValueType);

    const searchGroup = searchParams.get("group");
    const defaultGroup = searchGroup ? groupOptions.find(({value}) => searchGroup === value.toString()) : groupOptions[0];
    const [group, setGroup] = useState(defaultGroup);
    const [dates, setDates] = useState([]);
    const [data, {isLoading}] = useDataWithRoute(`/api/v6/report/business_unit_executive_overview_proc?client_id=${client.clientId}&period_id=${periodId}`);
    const [trend_data, {isLoading: trend_isLoading}] = useDataWithRoute(`/api/v6/report/business_unit_executive_overview_by_${group.type || "classification"}_proc?client_id=${client.clientId}&period_id=${periodId}`);

    useEffect(() => {
        const records = data?.recordsets || [];
        setChartDataCenter(records?.[0]?.map(data => {
            return {name: data["data_center"], y: data[valueType.value]};
        }) || []);
        setChartClassification(records?.[1]?.map(data => {
            return {name: data["classification"], y: data[valueType.value]};
        }) || []);
        setChartDeviceType(records?.[2]?.map(data => {
            return {name: data["device_type"], y: data[valueType.value]};
        }) || []);
        let operationalCatCheck = records?.[3]?.map(data => {
            return {name: data["operational_category"], y: data[valueType.value]};
        })
        if (operationalCatCheck?.length < 2 && !operationalCatCheck?.[0]?.["name"]) {
            operationalCatCheck = []
        }
        setChartOperationalCat(operationalCatCheck);

    }, [data]);
    useEffect(() => {
        createChartData(trend_data?.recordsets?.[0]);
    }, [trend_data, valueType])

    const createChartData = (recordsetData) => {
        setGraphLoading(true);

        const selectedDates = [];
        recordsetData?.forEach(d => {
            if (selectedDates.indexOf(d["collection_month"]) === -1) {
                selectedDates.push(d["collection_month"]);
            }
        });
        setDates(selectedDates);

        const newData = [];
        const distinctItems = [];
        recordsetData?.forEach(item => {
            if (distinctItems.indexOf(item[group.type || "classification"]) === -1) {
                distinctItems.push(item[group.type || "classification"]);
                newData.push({name: item[group.type || "classification"], type: "column", data: []});
            }
        });
        const total_used_arr = [];
        selectedDates?.forEach(date => {
            let total_used = 0.00;
            let total_usable = 0;
            newData.forEach(nd => {
                let found = false;
                recordsetData?.forEach(datum => {
                    if (date === datum["collection_month"]) {
                        if (datum[group.type || "classification"] === nd["name"]) {
                            total_used = total_used + parseInt(datum[valueType.usedType]);
                            total_usable += parseInt(datum[valueType.value])
                            found = true;
                        }
                    }
                });
                if (!found) {
                    nd["data"].push(0);
                } else {
                    nd["data"].push(total_usable);
                }
                total_usable = 0;
            });
            total_used_arr.push(total_used);
        });
        newData.push({
            name: "Total Used",
            type: "spline",
            data: total_used_arr,
            color: "#ff8d02"
        });

        setChartData(newData);
        setGraphLoading(false);
    };

    const onChange = (selectedValueType) => {
        setValueType(selectedValueType);
        setSearchParams({
            valueType: selectedValueType.value
        }, {replace: true});
        const records = data?.recordsets || [];
        setChartDataCenter(records?.[0]?.map(data => {
            return {name: data["data_center"], y: data[selectedValueType.value]};
        }) || []);
        setChartClassification(records?.[1]?.map(data => {
            return {name: data["classification"], y: data[selectedValueType.value]};
        }) || []);
        setChartDeviceType(records?.[2]?.map(data => {
            return {name: data["device_type"], y: data[selectedValueType.value]};
        }) || []);
        let operationalCatCheck = records?.[3]?.map(data => {
            return {name: data["operational_category"], y: data[valueType.value]};
        })
        if (operationalCatCheck?.length < 2 && !operationalCatCheck?.[0]?.["name"]) {
            operationalCatCheck = []
        }
        setChartOperationalCat(operationalCatCheck);

    };

    const onGroupChange = (selectedGroup) => {
        setGroup(selectedGroup);
        setSearchParams({
            group: selectedGroup.value
        }, {replace: true});
    };

    const renderCharts = useMemo(() => {
        if (chartOperationalCat?.length) {
            return (<><Col key={`pie-grid-classification`} xs={12} lg={3}>
                <Card color={theme}>
                    <CardHeader>
                        <VsiH5>{"Storage Capacity (GiB) Distribution by Classification"}</VsiH5>
                    </CardHeader>
                    <CardBody>
                        <PieGrid data={chartClassification || []}
                                 gridConfig={["Classification", "Usable"]}/>
                    </CardBody>
                </Card>
            </Col>
                <Col key={`pie-grid-device_type`} xs={12} lg={3}>
                    <Card color={theme}>
                        <CardHeader>
                            <VsiH5>{"Storage Capacity (GiB) Distribution by Device Type"}</VsiH5>
                        </CardHeader>
                        <CardBody>
                            <PieGrid data={chartDeviceType || []} gridConfig={["Device Type", "Usable"]}/>
                        </CardBody>
                    </Card>
                </Col>
                <Col key={`pie-grid-data_center`} xs={12} lg={3}>
                    <Card color={theme}>
                        <CardHeader>
                            <VsiH5>{"Storage Capacity (GiB) Distribution by Data Center"}</VsiH5>
                        </CardHeader>
                        <CardBody>
                            <PieGrid data={chartDataCenter || []} gridConfig={["Data Center", "Usable"]}/>
                        </CardBody>
                    </Card>
                </Col>
                <Col key={`pie-grid-category`} xs={12} lg={3}>
                    <Card color={theme}>
                        <CardHeader>
                            <VsiH5>{"Storage Capacity (GiB) Distribution by Operational Category"}</VsiH5>
                        </CardHeader>
                        <CardBody>
                            <PieGrid data={chartOperationalCat || []} gridConfig={["Operational Category", "Usable"]}/>
                        </CardBody>
                    </Card>
                </Col></>)
        } else {
            return (<><Col key={`pie-grid-classification`} xs={12} lg={4}>
                <Card color={theme}>
                    <CardHeader>
                        <VsiH5>{"Storage Capacity (GiB) Distribution by Classification"}</VsiH5>
                    </CardHeader>
                    <CardBody>
                        <PieGrid data={chartClassification || []}
                                 gridConfig={["Classification", "Usable"]}/>
                    </CardBody>
                </Card>
            </Col>
                <Col key={`pie-grid-device_type`} xs={12} lg={4}>
                    <Card color={theme}>
                        <CardHeader>
                            <VsiH5>{"Storage Capacity (GiB) Distribution by Device Type"}</VsiH5>
                        </CardHeader>
                        <CardBody>
                            <PieGrid data={chartDeviceType || []} gridConfig={["Device Type", "Usable"]}/>
                        </CardBody>
                    </Card>
                </Col>
                <Col key={`pie-grid-data_center`} xs={12} lg={4}>
                    <Card color={theme}>
                        <CardHeader>
                            <VsiH5>{"Storage Capacity (GiB) Distribution by Data Center"}</VsiH5>
                        </CardHeader>
                        <CardBody>
                            <PieGrid data={chartDataCenter || []} gridConfig={["Data Center", "Usable"]}/>
                        </CardBody>
                    </Card>
                </Col></>)
        }
    }, [data, chartOperationalCat]);

    return (
        <Card className={`report-card p-2 nav-card-${theme}`}>
            <VsiH4>Executive Overview</VsiH4>
            <VsiBubble noAutobot>
                <div className={"w-100 p-2 d-flex"}>
                    <VsiLabeledSelect key={"pie-chart-options"}
                                      isDisabled={isLoading || trend_isLoading}
                                      isLoading={isLoading || trend_isLoading}
                                      options={allUsableTypes}
                                      label={"Select Usable Type"}
                                      onChange={onChange}
                                      defaultValue={valueType}/>
                </div>
            </VsiBubble>
            <VsiBubble autobotName='StorageCapacityGraphs' unit={valueType.value} autobotInfo={{
                data: {
                    classificationPieChart: chartClassification,
                    deviceTypePieChart: chartDeviceType,
                    dataCenterPieChart: chartDataCenter,
                    operationalCatPieChart: chartOperationalCat
                }
            }}>
                <Row className={"mb-2"}>
                    {renderCharts}
                </Row>
            </VsiBubble>
            <VsiH5>Storage Capacity Trend</VsiH5>
            <VsiHr/>
            <VsiBubble noAutobot>
                <div className={"w-100 p-2 d-flex"}>
                    <VsiLabeledSelect key={`bar-chart-trend-report`}
                                      isDisabled={isLoading || graphLoading || trend_isLoading}
                                      isLoading={isLoading || graphLoading || trend_isLoading}
                                      options={groupOptions}
                                      onChange={onGroupChange}
                                      defaultValue={group}/>
                </div>
            </VsiBubble>
            <VsiBubble unit={group.label} autobotName='StackedLineColumnChart'>
                {isLoading || graphLoading || trend_isLoading ? <Spinner color={"primary"}/> : (
                    <StackedColumnChart categories={dates}
                                        stackedLabelsEnabled={false}
                                        colors={executiveOverviewColors}
                                        dataLabelsEnabled={false}
                                        height={600}
                                        series={chartData}/>
                )}
            </VsiBubble>
        </Card>
    );
}
